import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PostWriteForm from '../components/PostWriteForm';
import { getPost } from '../api/community';

// Handles /community/write?category=<id> (new) and /community/edit/:id (edit).
// Auth gate redirects to /login?redirect=<current path>.
// In edit mode, fetches the post first to pre-fill the form.

export default function CommunityWritePage({ mode }) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user, isAdmin, isLoading } = useAuth();

  const [post, setPost] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loadingPost, setLoadingPost] = useState(mode === 'edit');

  useEffect(() => {
    if (mode !== 'edit' || !id) return;
    let active = true;
    setLoadingPost(true);
    getPost(id)
      .then(data => { if (active) setPost(data.post); })
      .catch(err => { if (active) setLoadError(err); })
      .finally(() => { if (active) setLoadingPost(false); });
    return () => { active = false; };
  }, [mode, id]);

  // Defense-in-depth: server enforces ownership, but a client-side guard
  // prevents the form from briefly rendering for a different user before the
  // server's 403 fires.
  const userSub = user?.userId || user?.username;
  const ownershipDenied = mode === 'edit' && post && userSub
    && post.authorSub !== userSub && !isAdmin;

  // Wait for auth bootstrap
  if (isLoading) {
    return (
      <div className="container" style={{ paddingTop: 100, paddingBottom: 60, textAlign: 'center' }}>
        <p style={{ color: 'var(--text-muted)' }}>로딩 중...</p>
      </div>
    );
  }

  // Auth gate
  if (!user) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  // Edit-mode ownership gate (defense-in-depth before server 403)
  if (ownershipDenied) {
    return <Navigate to={`/community/post/${encodeURIComponent(id)}`} replace />;
  }

  return (
    <div className="container" style={{ paddingTop: 100, paddingBottom: 60, maxWidth: 800 }}>
      <title>{mode === 'edit' ? '글 수정' : '글 작성'} | KOA DETAILING</title>

      <h1 style={{ marginBottom: 24 }}>
        {mode === 'edit' ? '글 수정' : '글 작성'}
      </h1>

      {loadingPost && (
        <p style={{ color: 'var(--text-muted)' }}>글을 불러오는 중...</p>
      )}

      {loadError && !loadingPost && (
        <div style={{
          padding: 20, borderRadius: 'var(--radius)',
          background: 'rgba(255, 82, 82, 0.08)', border: '1px solid #ff5252',
        }}>
          <p style={{ color: '#ff5252', fontWeight: 700 }}>
            ❌ {loadError.message || '글을 불러올 수 없습니다'}
          </p>
        </div>
      )}

      {!loadingPost && !loadError && (
        <PostWriteForm
          mode={mode}
          initialPost={post}
          defaultCategory={searchParams.get('category') || 'review'}
        />
      )}
    </div>
  );
}
